import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import {
  Building2,
  Save,
  ArrowLeft,
  Loader2,
  FileText,
  Globe,
  Tag
} from 'lucide-react'
import { brandsService } from '@/services/brands'
import { ROUTES } from '@/utils/constants'
import { smartCacheInvalidation } from '@/utils/cacheInvalidation'
import { NavigationUtils } from '@/utils/navigation'
import toast from 'react-hot-toast'

const BrandCreate = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  // Create brand mutation
  const createBrandMutation = useMutation(
    (brandData) => brandsService.create(brandData),
    {
      onSuccess: () => {
        toast.success('Brand created successfully!')
        // Smart cache invalidation for real-time updates
        smartCacheInvalidation(queryClient, 'brand', 'create')
        NavigationUtils.navigateBackToSettings(navigate, 'brands')
      },
      onError: (error) => {
        toast.error('Failed to create brand')
        console.error('Error creating brand:', error)
      }
    }
  )

  const onSubmit = async (data) => {
    createBrandMutation.mutate(data)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'brands')}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Brands
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Brand</h1>
            <p className="mt-1 text-sm text-gray-600">
              Add a new brand to the system
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Brand Information</h3>
            <p className="mt-1 text-sm text-gray-600">
              Basic brand details and configuration
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Brand Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Brand Name *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('name', {
                    required: 'Brand name is required',
                    minLength: {
                      value: 2,
                      message: 'Brand name must be at least 2 characters',
                    },
                  })}
                  type="text"
                  className={`input pl-10 ${
                    errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter brand name"
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1 relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  {...register('description')}
                  rows={4}
                  className="input pl-10 resize-none"
                  placeholder="Enter brand description"
                />
              </div>
            </div>



            {/* Active Status */}
            <div>
              <label className="flex items-center">
                <input
                  {...register('is_active')}
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Active Brand</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Inactive brands are hidden from users and cannot be used
              </p>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'brands')}
            className="btn-outline btn-md"
            disabled={createBrandMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary btn-md flex items-center"
            disabled={createBrandMutation.isLoading}
          >
            {createBrandMutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Brand
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default BrandCreate
